import { getAdblockState } from "core/utils/adblock";

/**
 * Собирает причину неудачного показа рекламы для отправки в импрессию.
 *
 * Зачем: в отчётах видно только событие `error`, и по нему нельзя понять,
 * битый файл креатива, не отвечает VAST, или браузер не умеет кодек.
 * Причина уезжает отдельным полем и ложится рядом с кампанией, креативом,
 * местом и устройством, так что сразу видно, у какого именно креатива
 * проблема и на каких браузерах.
 *
 * Данные уходят на сервер, поэтому здесь только короткие технические
 * значения: код, описание от браузера и адрес файла без параметров
 * запроса (в них бывают токены доступа).
 *
 * К каждой причине прикладывается признак блокировщика рекламы: без него
 * SRC_NOT_SUPPORTED на загрузке выглядит одинаково и у битого файла, и у
 * запроса, который фильтр не выпустил из браузера.
 */

/** Коды MediaError, словами. */
const MEDIA_ERROR_NAMES = {
  1: "ABORTED",
  2: "NETWORK",
  3: "DECODE",
  4: "SRC_NOT_SUPPORTED",
};

const MESSAGE_LIMIT = 300;

function cleanUrl(url) {
  if (!url) return undefined;

  try {
    const { origin, pathname } = new URL(url, window.location.origin);

    return `${origin}${pathname}`;
  } catch (error) {
    return String(url).split("?")[0].slice(0, MESSAGE_LIMIT);
  }
}

function text(value) {
  if (value === undefined || value === null) return undefined;

  const string = typeof value === "string" ? value : String(value);

  return string ? string.slice(0, MESSAGE_LIMIT) : undefined;
}

/**
 * Ошибка html-элемента video: наш собственный формат видеорекламы.
 *
 * `event.target.error` это MediaError, у него есть код и, в части
 * браузеров, пояснение. Дополняем состоянием загрузки: `networkState` 3
 * означает, что источник так и не выбран, то есть файл вообще не
 * открылся, а `readyState` 0 что не приехало ни одного кадра.
 */
export function describeVideoError(event, { source = "video" } = {}) {
  const element = event?.target;
  const mediaError = element?.error;
  const code = mediaError?.code;

  /**
   * Где именно оборвалось. Это главное для разбора: ролик не открылся
   * вовсе (нечего показывать) или сломался на середине уже засчитанного
   * показа. Первое лечится перезаливкой файла, второе почти всегда сеть
   * или сам файл, обрезанный при загрузке.
   */
  const position = Number(element?.currentTime) || 0;
  const stage = position > 0 ? "playback" : "load";

  const parts = [
    mediaError?.message,
    element ? `networkState=${element.networkState}` : null,
    element ? `readyState=${element.readyState}` : null,
    position ? `position=${position.toFixed(1)}` : null,
    element?.duration && Number.isFinite(element.duration)
      ? `duration=${element.duration.toFixed(1)}`
      : null,
  ].filter(Boolean);

  return {
    source,
    stage,
    code: code ? MEDIA_ERROR_NAMES[code] || String(code) : "UNKNOWN",
    message: text(parts.join(" ")),
    url: cleanUrl(element?.currentSrc || element?.src),
    adblock: getAdblockState(),
  };
}

/**
 * Ошибка VAST или рекламного SDK: там приходит обычный Error или строка.
 */
export function describeAdError(error, { source, code, url, stage } = {}) {
  const message = typeof error === "string" ? error : error?.message;

  return {
    source,
    stage,
    code: text(code || error?.code) || "UNKNOWN",
    message: text(message),
    url: cleanUrl(url),
    adblock: getAdblockState(),
  };
}
